import React, { useCallback, useLayoutEffect, useState } from 'react'
import { Box, SvgIcon, useTheme } from '@mui/material'
import { SxProps, Theme } from '@mui/material/styles'
import { ReactComponent as ArrowDownIcon } from '../../../assets/images/arrows/down.svg'
import { toSxArray } from '../../utils/toSxArray'

type Orientation = 'vertical' | 'horizontal'

interface ScrollWrapperProps {
    children: React.ReactNode
    orientation?: Orientation
    disabled?: boolean
    onScroll?: () => void
    arrowColor?: string
    fadeColor?: string
    arrowSize?: string
    arrowOffset?: number
    hideScrollbar?: boolean
    sx?: SxProps<Theme>
    containerSx?: SxProps<Theme>
}

const FADE_SIZE = '50px'

const resolvePaletteColor = (theme: Theme, value?: string): string | undefined => {
    if (!value) return undefined
    const resolved = value
        .split('.')
        .reduce<unknown>(
            (node, key) => (node && typeof node === 'object' ? (node as Record<string, unknown>)[key] : undefined),
            theme.palette
        )
    return typeof resolved === 'string' ? resolved : value
}

export const ScrollWrapper: React.FC<ScrollWrapperProps> = ({
    children,
    orientation = 'vertical',
    disabled = false,
    onScroll,
    arrowColor,
    fadeColor,
    arrowSize,
    arrowOffset,
    hideScrollbar = false,
    sx,
    containerSx,
}) => {
    const theme = useTheme()
    const [node, setNode] = useState<HTMLDivElement | null>(null)
    const [showStartArrow, setShowStartArrow] = useState(false)
    const [showEndArrow, setShowEndArrow] = useState(false)

    const isHorizontal = orientation === 'horizontal'

    const updateEdges = useCallback(() => {
        if (!node) return
        const scrollStart = isHorizontal ? node.scrollLeft : node.scrollTop
        const scrollSize = isHorizontal ? node.scrollWidth : node.scrollHeight
        const clientSize = isHorizontal ? node.clientWidth : node.clientHeight
        setShowStartArrow(scrollStart > 1)
        setShowEndArrow(scrollSize - scrollStart > clientSize + 1)
    }, [node, isHorizontal])

    useLayoutEffect(() => {
        if (!disabled) updateEdges()
    })

    useLayoutEffect(() => {
        if (!node || disabled) return
        // @ts-expect-error ts version mismatch
        const observer = new ResizeObserver(updateEdges)
        observer.observe(node)
        window.addEventListener('resize', updateEdges)
        return () => {
            observer.disconnect()
            window.removeEventListener('resize', updateEdges)
        }
    }, [node, disabled, updateEdges])

    const handleScroll = () => {
        onScroll?.()
        updateEdges()
    }

    if (disabled) {
        return <Box sx={sx}>{children}</Box>
    }

    const resolvedArrowColor = arrowColor || theme.palette.figma.grayscale.stroke
    const resolvedArrowSize = arrowSize || '16px'
    const gradientBase = resolvePaletteColor(theme, fadeColor) || theme.palette.figma.grayscale.contentWrapper
    const offset = arrowOffset ?? 10

    const edgeBase = isHorizontal
        ? { top: 0, bottom: 0, width: FADE_SIZE, alignItems: 'center' }
        : { left: 0, right: 0, height: FADE_SIZE, justifyContent: 'center' }

    const edges = [
        {
            show: showStartArrow,
            rotate: isHorizontal ? 'rotate(90deg)' : 'rotate(180deg)',
            sx: isHorizontal
                ? {
                      left: `-${offset}px`,
                      justifyContent: 'flex-start',
                      background: `linear-gradient(to left, transparent, ${gradientBase})`,
                  }
                : {
                      top: `-${offset}px`,
                      alignItems: 'flex-start',
                      background: `linear-gradient(to top, transparent, ${gradientBase})`,
                  },
        },
        {
            show: showEndArrow,
            rotate: isHorizontal ? 'rotate(-90deg)' : 'none',
            sx: isHorizontal
                ? {
                      right: `-${offset}px`,
                      justifyContent: 'flex-end',
                      background: `linear-gradient(to right, transparent, ${gradientBase})`,
                  }
                : {
                      bottom: `-${offset}px`,
                      alignItems: 'flex-end',
                      background: `linear-gradient(to bottom, transparent, ${gradientBase})`,
                  },
        },
    ]

    return (
        <Box sx={[{ position: 'relative' }, ...toSxArray(sx)]}>
            {edges.map(
                ({ show, rotate, sx: edgeSx }, index) =>
                    show && (
                        <Box
                            key={index}
                            sx={{
                                position: 'absolute',
                                display: 'flex',
                                pointerEvents: 'none',
                                zIndex: 1,
                                ...edgeBase,
                                ...edgeSx,
                            }}
                        >
                            <SvgIcon
                                component={ArrowDownIcon}
                                sx={{ transform: rotate, fontSize: resolvedArrowSize, color: resolvedArrowColor }}
                            />
                        </Box>
                    )
            )}
            <Box
                ref={setNode}
                onScroll={handleScroll}
                sx={[
                    {
                        overflowX: isHorizontal ? 'auto' : 'hidden',
                        overflowY: isHorizontal ? 'hidden' : 'auto',
                    },
                    hideScrollbar && {
                        scrollbarWidth: 'none',
                        '&::-webkit-scrollbar': { display: 'none' },
                    },
                    ...toSxArray(containerSx),
                ]}
            >
                {children}
            </Box>
        </Box>
    )
}
